import { Link } from "@tanstack/react-router";
import { ChevronRight, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import type * as S from "@releo/shared";
import { timeAgo } from "@/lib/utils";
import { PlatformIcon, StatusBadge } from "./common";
import { Table, Td, Th } from "./ui";

/**
 * Builds table; with `appNames`, adds an app column (builds of several apps). With `onTogglePublic`,
 * adds a switch that lists the build on the public page of the app.
 */
export function BuildsTable({ builds, appNames, onTogglePublic }: { builds: S.Build[]; appNames?: Map<string, string>; onTogglePublic?: (build: S.Build) => void }) {
  const { t, i18n } = useTranslation();
  return (
    <Table>
      <thead>
        <tr>
          {appNames && <Th>{t("apps.app")}</Th>}
          <Th>{t("build.version")}</Th>
          <Th>{t("build.status")}</Th>
          <Th className="hidden md:table-cell">{t("build.track")}</Th>
          <Th className="hidden lg:table-cell">{t("build.commit")}</Th>
          <Th className="hidden sm:table-cell">{t("build.uploadedAt")}</Th>
          <Th />
        </tr>
      </thead>
      <tbody>
        {builds.map((b) => {
          const params = { appId: b.appId, buildId: b.id };
          return (
            <tr key={b.id} className="hover:bg-muted/50">
              {appNames && (
                <Td className="font-medium">
                  <Link to="/apps/$appId" params={{ appId: b.appId }} className="hover:underline">
                    {appNames.get(b.appId) ?? "—"}
                  </Link>
                </Td>
              )}
              <Td>
                <Link to="/apps/$appId/builds/$buildId" params={params} className="flex items-center gap-2 font-medium">
                  <PlatformIcon platform={b.platform} />
                  {b.version}
                  <span className="font-normal text-fg-muted">({b.buildNumber})</span>
                </Link>
              </Td>
              <Td>
                <StatusBadge status={b.status} />
              </Td>
              <Td className="hidden text-fg-muted md:table-cell">{b.track ?? "—"}</Td>
              <Td className="hidden font-mono text-xs text-fg-muted lg:table-cell">{b.commitSha?.slice(0, 8) ?? "—"}</Td>
              <Td className="hidden text-fg-muted sm:table-cell">
                <span title={new Date(b.createdAt).toLocaleString(i18n.language)}>{timeAgo(b.createdAt, i18n.language)}</span>
                {b.uploadedBy && <span className="block text-xs">{b.uploadedBy.name}</span>}
              </Td>
              <Td className="text-right">
                <span className="inline-flex items-center gap-1">
                  {onTogglePublic && (
                    <button
                      onClick={() => onTogglePublic(b)}
                      title={t(b.public ? "publish.buildPublic" : "publish.buildHidden")}
                      aria-label={t(b.public ? "publish.buildPublic" : "publish.buildHidden")}
                      aria-pressed={b.public}
                      className={b.public ? "rounded p-1 text-brand-600 hover:bg-muted" : "rounded p-1 text-fg-muted/50 hover:bg-muted hover:text-fg"}
                    >
                      {b.public ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                  )}
                  <Link to="/apps/$appId/builds/$buildId" params={params} className="inline-flex text-fg-muted hover:text-fg">
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </span>
              </Td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
